import { useState, type ComponentType, type CSSProperties, type SVGProps } from 'react';
import classNames from 'classnames';
import {
  ArrowDown,
  ArrowUp,
  BookOpen,
  Building2,
  Check,
  CreditCard,
  ExternalLink,
  FileText,
  Info,
  Puzzle,
  Sparkles,
  Target,
  TriangleAlert,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import type { AgentBlock, AgentChoiceOption } from '../../agent/agent-api';
import { Badge } from '../../components/Badge/Badge';
import { Button } from '../../components/Button/Button';
import styles from './AssistantBlocks.module.css';

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

/** How a tour step's status reads on its card; a step not reached yet shows nothing. */
const TOUR_STATUS: Record<string, string | null> = {
  PENDING: null,
  VIEWED: 'Seen',
  DONE: 'Done',
  SKIPPED: 'Skipped',
};

/** lucide icons for preview-card headers, keyed by the backend `icon` string. */
const PREVIEW_ICONS: Record<string, IconComponent> = {
  client: Building2,
  subscription: CreditCard,
  component: Puzzle,
  puzzle: Puzzle,
  docs: BookOpen,
  objective: Target,
};

const CALLOUT_ICONS: Record<NonNullable<AgentCalloutTone>, IconComponent> = {
  info: Info,
  accent: Sparkles,
  warn: TriangleAlert,
  danger: TriangleAlert,
};

type AgentCalloutTone = 'info' | 'accent' | 'warn' | 'danger';

function Delta({ up, value }: { up?: boolean; value: string }) {
  const Icon = up ? ArrowUp : ArrowDown;
  return (
    <span className={classNames(styles.Delta, up ? styles.DeltaUp : styles.DeltaDown)}>
      <Icon aria-hidden="true" />
      {value}
    </span>
  );
}

export interface AgentBlockViewProps {
  block: AgentBlock;
  /** Called when the user picks a choice option (choice blocks only). */
  onChoice?: (choiceId: string, option: AgentChoiceOption) => void;
  /** Called with every ticked option when a multiple-choice block is continued. */
  onChoices?: (choiceId: string, options: AgentChoiceOption[]) => void;
  /** The value already picked for this choice block, if any. */
  pickedValue?: string;
  /** The values already picked for a multiple-choice block. */
  pickedValues?: string[];
  /** Disable interactive controls while a turn is streaming. */
  disabled?: boolean;
  /**
   * The conversation has moved past this block (it is not on the latest
   * message). An unanswered picker then shows only its question, so the one
   * still waiting for an answer is the only one that can be clicked.
   */
  stale?: boolean;
}

/** Render one structured answer block as a card. */
export function AgentBlockView({
  block,
  onChoice,
  onChoices,
  pickedValue,
  pickedValues,
  disabled,
  stale,
}: AgentBlockViewProps) {
  switch (block.kind) {
    case 'metrics':
      return (
        <div className={styles.Block}>
          {block.label && <div className={styles.BlockLabel}>{block.label}</div>}
          <div className={styles.Metrics}>
            {block.items.map((item, index) => (
              <div key={index} className={styles.Metric}>
                <div className={styles.MetricLabel}>{item.label}</div>
                <div className={styles.MetricValue}>{item.value}</div>
                {item.delta && <Delta up={item.up} value={item.delta} />}
              </div>
            ))}
          </div>
        </div>
      );

    case 'chart': {
      const max = Math.max(...block.data.map((point) => point.value), 1);
      return (
        <div className={styles.Block}>
          {block.label && <div className={styles.BlockLabel}>{block.label}</div>}
          <div className={styles.Chart}>
            <div className={styles.Bars}>
              {block.data.map((point, index) => (
                <div key={index} className={styles.BarCol}>
                  <span
                    className={classNames(styles.Bar, {
                      [styles.BarHi]: !point.down && index === block.data.length - 1,
                      [styles.BarDown]: point.down,
                    })}
                    style={{ '--ah-bar': `${18 + (point.value / max) * 82}%` } as CSSProperties}
                    title={`${point.x}: ${point.valueLabel ?? point.value}`}
                  />
                  <span className={styles.BarLabel}>{point.x}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    case 'sources':
      return (
        <div className={styles.Block}>
          {block.label && <div className={styles.BlockLabel}>{block.label}</div>}
          <ul className={styles.Sources}>
            {block.items.map((item, index) => (
              <li key={index} className={styles.Source}>
                <FileText aria-hidden="true" className={styles.SourceIcon} />
                {item.url ? (
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.SourceTitle}
                  >
                    {item.title}
                    <ExternalLink aria-hidden="true" className={styles.SourceExternal} />
                  </a>
                ) : (
                  <span className={styles.SourceTitle} title="Internal document: no public link">
                    {item.title}
                  </span>
                )}
                <Badge
                  badgeType={item.restricted ? 'outline' : 'secondary'}
                  indicator={item.restricted ? 'orange' : undefined}
                  size="small"
                  className={styles.SourceAudience}
                  title={item.restricted ? 'Not every customer can read this' : undefined}
                >
                  {item.audience}
                </Badge>
              </li>
            ))}
          </ul>
        </div>
      );

    case 'table':
      return (
        <div className={styles.Block}>
          {block.label && <div className={styles.BlockLabel}>{block.label}</div>}
          <div className={styles.TableWrap}>
            <table className={styles.Table}>
              <thead>
                <tr>
                  {block.columns.map((column, index) => (
                    <th key={index} className={column.numeric ? styles.Numeric : undefined}>
                      {column.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {block.rows.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {row.map((cell, cellIndex) => (
                      <td
                        key={cellIndex}
                        className={block.columns[cellIndex]?.numeric ? styles.Numeric : undefined}
                      >
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );

    case 'preview': {
      const Icon = (block.icon && PREVIEW_ICONS[block.icon]) || Puzzle;
      return (
        <div className={styles.Preview}>
          <div className={styles.PreviewHead}>
            <span className={styles.PreviewTitle}>
              <Icon aria-hidden="true" />
              {block.title}
            </span>
            {block.tag && <span className={styles.PreviewTag}>{block.tag}</span>}
          </div>
          <div className={styles.PreviewBody}>
            <div className={styles.Fields}>
              {block.fields.map((field, index) => (
                <div key={index} className={styles.Field}>
                  <span className={styles.FieldKey}>{field.label}</span>
                  <span className={styles.FieldValue}>{field.value}</span>
                </div>
              ))}
            </div>
            {block.note && (
              <div className={classNames(styles.Callout, styles.CalloutAccent, styles.PreviewNote)}>
                <Sparkles aria-hidden="true" />
                <span>{block.note}</span>
              </div>
            )}
          </div>
        </div>
      );
    }

    case 'tour_controls':
      return (
        <div className={styles.Preview}>
          <div className={styles.PreviewHead}>
            <span className={styles.PreviewTitle}>
              <Sparkles aria-hidden="true" />
              {block.title}
            </span>
            {block.paused && <Badge badgeType="secondary">Paused</Badge>}
          </div>
          <div className={styles.PreviewBody}>
            <ol className={styles.TourSteps}>
              {block.steps.map((step) => (
                <li
                  key={step.key}
                  className={classNames(styles.TourStep, {
                    [styles.TourStepCurrent]: step.key === block.stepKey,
                  })}
                  aria-current={step.key === block.stepKey ? 'step' : undefined}
                >
                  <span>{step.title}</span>
                  {TOUR_STATUS[step.status] && (
                    <Badge
                      badgeType={step.status === 'DONE' ? undefined : 'secondary'}
                      indicator={step.status === 'DONE' ? 'green' : undefined}
                    >
                      {TOUR_STATUS[step.status]}
                    </Badge>
                  )}
                </li>
              ))}
            </ol>
            {block.complete && block.made.length > 0 && (
              <div className={styles.TourMade}>
                <span className={styles.TourMadeLabel}>Made in your tour</span>
                {block.made.map((item, index) =>
                  item.url ? (
                    <Link key={index} to={item.url} className={styles.ActionLink}>
                      {item.label}
                    </Link>
                  ) : (
                    <span key={index}>{item.label}</span>
                  ),
                )}
              </div>
            )}
          </div>
        </div>
      );

    case 'plan_picker':
      return (
        <div className={styles.Plans}>
          <div className={styles.PlansTitle}>{block.title}</div>
          <div className={styles.PlanGrid}>
            {block.options.map((option) => (
              <div
                key={option.code}
                className={classNames(styles.Plan, {
                  [styles.PlanRecommended]: option.recommended,
                  [styles.PlanSoon]: !option.available,
                })}
              >
                <div className={styles.PlanHead}>
                  <span className={styles.PlanName}>{option.name}</span>
                  {option.kind === 'ADD_ON' && (
                    <Badge badgeType="outline" size="small">
                      Add-on
                    </Badge>
                  )}
                </div>
                <div className={styles.PlanPrice}>{option.price ?? 'Price on request'}</div>
                {option.includes.length > 0 && (
                  <div className={styles.PlanIncludes}>{option.includes.join(' · ')}</div>
                )}
                <div className={styles.PlanTags}>
                  {option.recommended && <Badge indicator="green">Recommended</Badge>}
                  {!option.available && <Badge badgeType="secondary">Coming soon</Badge>}
                </div>
                {option.reason && <p className={styles.PlanReason}>{option.reason}</p>}
              </div>
            ))}
          </div>
          {block.note && <p className={styles.PlansNote}>{block.note}</p>}
        </div>
      );

    case 'price_card':
      return (
        <div className={styles.Preview}>
          <div className={styles.PreviewHead}>
            <span className={styles.PreviewTitle}>
              <CreditCard aria-hidden="true" />
              {block.title}
            </span>
          </div>
          <div className={styles.PreviewBody}>
            <div className={styles.PriceLines}>
              {block.lines.map((line, index) => (
                <div key={index} className={styles.PriceLine}>
                  <span>{line.label}</span>
                  <span className={styles.PriceAmount}>{line.amount}</span>
                </div>
              ))}
              {block.rates.map((rate, index) => (
                <div
                  key={`rate-${index}`}
                  className={classNames(styles.PriceLine, styles.PriceRate)}
                >
                  <span>{rate.label}</span>
                  <span>{rate.rate}</span>
                </div>
              ))}
              {block.total && (
                <div className={classNames(styles.PriceLine, styles.PriceTotal)}>
                  <span>Total after your trial</span>
                  <span className={styles.PriceAmount}>{block.total}</span>
                </div>
              )}
            </div>
            {block.note && (
              <div className={classNames(styles.Callout, styles.PreviewNote)}>
                <Info aria-hidden="true" />
                <span>{block.note}</span>
              </div>
            )}
          </div>
        </div>
      );

    case 'callout': {
      const tone = block.tone ?? 'info';
      const Icon = CALLOUT_ICONS[tone];
      return (
        <div
          className={classNames(styles.Callout, {
            [styles.CalloutAccent]: tone === 'accent',
            [styles.CalloutWarn]: tone === 'warn',
            [styles.CalloutDanger]: tone === 'danger',
          })}
        >
          <Icon aria-hidden="true" />
          <span>{block.body}</span>
        </div>
      );
    }

    case 'actions':
      return (
        <div className={styles.Actions}>
          {block.items.map((action, index) =>
            action.href ? (
              <Link
                key={index}
                to={action.href}
                className={classNames(styles.ActionLink, {
                  [styles.ActionLinkPrimary]: action.variant === 'primary',
                })}
              >
                {action.label}
              </Link>
            ) : (
              <Button
                key={index}
                type="button"
                variant={action.variant === 'primary' ? 'primary' : 'outline'}
                size="sm"
                disabled
              >
                {action.label}
              </Button>
            ),
          )}
        </div>
      );

    case 'choice':
      return (
        <ChoiceBlockView
          block={block}
          onChoice={onChoice}
          onChoices={onChoices}
          picked={pickedValues ?? (pickedValue === undefined ? [] : [pickedValue])}
          disabled={disabled}
          stale={stale}
        />
      );

    default:
      return null;
  }
}

type ChoiceBlock = Extract<AgentBlock, { kind: 'choice' }>;

/**
 * An inline picker. One answer sends on the click. A `multiple` picker ticks
 * any number of options and sends them together on Continue; an `exclusive`
 * option ("None yet", "Skip") is the whole answer and sends at once.
 */
function ChoiceBlockView({
  block,
  onChoice,
  onChoices,
  picked,
  disabled,
  stale,
}: {
  block: ChoiceBlock;
  onChoice?: (choiceId: string, option: AgentChoiceOption) => void;
  onChoices?: (choiceId: string, options: AgentChoiceOption[]) => void;
  picked: string[];
  disabled?: boolean;
  stale?: boolean;
}) {
  const [ticked, setTicked] = useState<string[]>([]);
  const answered = block.options.filter((option) => picked.includes(option.value));
  if (answered.length > 0) {
    return (
      <div className={classNames(styles.Choice, styles.ChoiceDone)}>
        <div className={styles.ChoiceQuestion}>
          <Sparkles aria-hidden="true" />
          {block.question}
        </div>
        <span className={styles.ChoicePicked}>
          {answered.map((option) => (
            <span key={option.value} className={styles.ChoicePickedTag}>
              <Check aria-hidden="true" />
              {option.label}
            </span>
          ))}
        </span>
      </div>
    );
  }
  if (stale) {
    return (
      <div className={classNames(styles.Choice, styles.ChoiceDone)}>
        <div className={classNames(styles.ChoiceQuestion, styles.ChoiceQuestionDone)}>
          <Check aria-hidden="true" />
          {block.question}
        </div>
      </div>
    );
  }

  const isCards = block.variant === 'cards';
  const multiple = block.multiple === true;
  const toggle = (option: AgentChoiceOption) =>
    setTicked((current) =>
      current.includes(option.value)
        ? current.filter((value) => value !== option.value)
        : [...current, option.value],
    );
  const send = () => {
    const chosen = block.options.filter((option) => ticked.includes(option.value));
    if (chosen.length > 0) onChoices?.(block.choiceId, chosen);
  };

  return (
    <div className={styles.Choice} role="group" aria-label={block.question}>
      <div className={styles.ChoiceQuestion}>
        <Sparkles aria-hidden="true" />
        {block.question}
      </div>
      <div className={isCards ? styles.ChoiceCards : styles.ChoicePills}>
        {block.options.map((option) => {
          const isTicked = ticked.includes(option.value);
          const ticks = multiple && !option.exclusive;
          return (
            <Button
              key={option.value}
              type="button"
              variant="outline"
              size="sm"
              className={classNames(isCards ? styles.ChoiceCard : styles.ChoicePill, {
                [styles.ChoiceTicked]: isTicked,
              })}
              disabled={disabled}
              aria-pressed={ticks ? isTicked : undefined}
              onClick={() => (ticks ? toggle(option) : onChoice?.(block.choiceId, option))}
            >
              {isCards ? (
                <span className={styles.ChoiceCardText}>
                  <span className={styles.ChoiceCardLabel}>
                    {ticks && isTicked && <Check aria-hidden="true" />}
                    {option.label}
                  </span>
                  {option.description && (
                    <span className={styles.ChoiceCardDesc}>{option.description}</span>
                  )}
                </span>
              ) : (
                <>
                  {ticks && isTicked && <Check aria-hidden="true" />}
                  {option.label}
                </>
              )}
            </Button>
          );
        })}
      </div>
      {multiple && (
        <div className={styles.ChoiceContinue}>
          <Button
            type="button"
            variant="primary"
            size="sm"
            disabled={disabled || ticked.length === 0}
            onClick={send}
          >
            {ticked.length > 1 ? `Continue with ${ticked.length}` : 'Continue'}
          </Button>
        </div>
      )}
      <p className={styles.ChoiceHint}>
        {multiple
          ? 'Pick all that apply, then continue, or type your own answer below.'
          : 'Pick one, or type your own answer below.'}
      </p>
    </div>
  );
}
